import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, User, ShoppingBag, FileText, Home, UserCheck, ShoppingCart, LogOut, Calendar, MessageCircle, Mail, BookOpen, HelpCircle, Info, ChevronDown, Moon, Sun, LifeBuoy, Calculator, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { navItems, getUserMenuItems } from './headerNavConfig';

const ResponsiveHeader = ({ onSearch, searchTerm, onReset, onSearchClick = () => {} }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debug: Log cart changes
  useEffect(() => {
    console.log('ResponsiveHeader - Cart updated:', cart);
  }, [cart]);

  // Fetch site settings
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        const response = await fetch(`${API_BASE}/api/site-settings`);
        if (response.ok) {
          const settings = await response.json();
          setSiteSettings(settings);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSiteSettings();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent body scroll when menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleLogout = () => {
    logout();
    closeMobileMenu();
    setIsUserMenuOpen(false);
    navigate('/login');
  };


  const handleHomeClick = (e) => {
    closeMobileMenu();
    
    console.log('🏠 [LOGO CLICK] Current path:', location.pathname);
    console.log('🏠 [LOGO CLICK] Current scroll position:', window.pageYOffset);
    
    // If already on home page, prevent navigation and just scroll smoothly
    if (location.pathname === '/') {
      e.preventDefault(); // Prevent Link navigation
      console.log('✅ [LOGO CLICK] On homepage - preventing default navigation');
      
      // Reset filters if callback provided
      if (onReset) {
        console.log('🔄 [LOGO CLICK] Resetting filters');
        onReset();
      }
      
      // Custom smooth scroll animation (since browser's smooth scroll is being overridden)
      console.log('🚀 [LOGO CLICK] Starting CUSTOM smooth scroll animation...');
      
      const startPosition = window.pageYOffset || document.documentElement.scrollTop;
      const startTime = performance.now();
      const duration = 800; // 800ms animation
      
      console.log('📍 [LOGO CLICK] Starting position:', startPosition);
      
      // Easing function for smooth deceleration
      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = easeInOutCubic(progress);
        
        const currentPosition = startPosition * (1 - easeProgress);
        
        window.scrollTo(0, currentPosition);
        
        console.log(`📊 [CUSTOM SCROLL] Progress: ${(progress * 100).toFixed(1)}% | Position: ${Math.round(currentPosition)}px`);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          console.log('🏁 [LOGO CLICK] Custom scroll animation complete!');
        }
      };
      
      requestAnimationFrame(animateScroll);
      
    } else {
      // If on different page, allow normal navigation
      console.log('🔀 [LOGO CLICK] Navigating to homepage from:', location.pathname);
      // Reset will be called after navigation in useEffect
      if (onReset) {
        onReset();
      }
    }
  };

  return (
    <>
      {/* Top Banner - Only show to users and non-logged-in visitors, not trainers */}
      {(!user || user.role !== 'trainer') && (
        <Link 
          to="/register?type=trainer" 
          className="block bg-gradient-to-r from-red-600 via-red-800 to-red-700 text-white text-center py-2 sm:py-3 font-bold tracking-wide shadow-lg text-xs sm:text-sm hover:from-red-700 hover:via-black hover:to-red-700 transition-all cursor-pointer"
        >
          <span className="text-yellow-300">✨</span> LIST YOURSELF AS A TRAINER <span className="text-yellow-300">✨</span>
        </Link>
      )}

      {/* Header */}
      <header className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b-4 border-gradient-to-r from-red-500 to-red-800 dark:from-red-600 dark:to-red-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex-shrink-0 transform hover:scale-105 transition-transform"
              onClick={handleHomeClick}
            >
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'}
                alt="TrainerList" 
                className="h-10 sm:h-12 md:h-14 w-auto object-contain transition-all duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={item.path === '/' ? handleHomeClick : undefined}
                  className={`font-semibold transition-colors ${
                    item.highlight
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-800'
                      : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Search Bar - Hidden on small mobile, visible from sm up */}
            {onSearch && (
              <div className="hidden sm:flex flex-1 max-w-md lg:max-w-xl mx-2 lg:mx-4">
                <div className="relative w-full">
                  <button 
                    onClick={onSearchClick}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-600 dark:text-red-400 hover:text-black dark:hover:text-red-300 transition-colors cursor-pointer z-10"
                  >
                    <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                  <Input
                    type="text"
                    placeholder="Search trainers, location, specialization..."
                    value={searchTerm}
                    onChange={(e) => onSearch(e.target.value)}
                    onClick={onSearchClick}
                    className="pl-8 sm:pl-10 pr-3 py-2 text-sm rounded-full border-2 border-red-200 dark:border-red-900 focus:border-red-500 dark:focus:border-red-600 bg-white dark:bg-gray-800 dark:text-gray-100 shadow-md hover:shadow-lg transition-shadow w-full cursor-pointer"
                    readOnly
                  />
                </div>
              </div>
            )}

            {/* User Menu & Mobile Toggle */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Search Button - Visible only on small screens */}
              {onSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onSearchClick}
                  className="sm:hidden rounded-full hover:scale-110 transition-transform"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-800 flex items-center justify-center shadow-lg">
                    <Search className="w-5 h-5 text-white" />
                  </div>
                </Button>
              )}

              {/* Theme Toggle Button */}
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={toggleTheme}
                className="rounded-full hover:scale-110 transition-all duration-300 hidden lg:flex"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-400 to-red-800 dark:from-red-600 dark:to-red-800 flex items-center justify-center shadow-lg transition-all duration-300">
                  {theme === 'light' ? (
                    <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  ) : (
                    <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  )}
                </div>
              </Button>

              {/* Cart Icon - Always Visible */}
              <Link to="/shop/cart" className="hidden lg:block relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="rounded-full hover:scale-110 transition-transform relative"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500 to-red-800 flex items-center justify-center shadow-lg">
                    <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                    {cart?.item_count || 0}
                  </span>
                </Button>
              </Link>

              {/* User Icon/Menu - Desktop */}
              {user ? (
                <div className="hidden lg:block relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 p-2 rounded-full hover:bg-red-50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-800 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-red-200 dark:border-gray-700 overflow-hidden z-[9999] animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* User Info Header */}
                      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-4">
                        <p className="font-bold text-lg">{user?.name}</p>
                        <p className="text-sm opacity-90">{user?.email}</p>
                        <p className="text-xs mt-1 bg-white/20 px-2 py-1 rounded-full inline-block capitalize">
                          {user?.role}
                        </p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        {getUserMenuItems(user).map((item) => {
                          const Icon = item.icon;
                          return (
                            <button
                              key={item.path}
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                navigate(item.path);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-red-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all group text-left"
                            >
                              <Icon className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                              <span className="text-gray-700 dark:text-gray-300 font-medium group-hover:text-red-700 dark:group-hover:text-red-400">
                                {item.label}
                              </span>
                            </button>
                          );
                        })}

                        {/* Divider */}
                        <div className="my-2 border-t border-gray-200 dark:border-gray-700"></div>

                        {/* Additional Links */}
                        <Link
                          to="/about"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-red-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all group"
                        >
                          <Info className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium group-hover:text-red-700 dark:group-hover:text-red-400">
                            About Us
                          </span>
                        </Link>

                        <Link
                          to="/blog"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-red-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all group"
                        >
                          <BookOpen className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium group-hover:text-red-700 dark:group-hover:text-red-400">
                            Blog
                          </span>
                        </Link>

                        <Link
                          to="/contact"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gradient-to-r hover:from-red-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all group"
                        >
                          <HelpCircle className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium group-hover:text-red-700 dark:group-hover:text-red-400">
                            Help & Support
                          </span>
                        </Link>

                        {/* Divider */}
                        <div className="my-2 border-t border-gray-200 dark:border-gray-700"></div>

                        {/* Logout */}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all group text-left"
                        >
                          <LogOut className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform" />
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            Logout
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="hidden lg:block">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="rounded-full hover:scale-110 transition-transform"
                  >
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500 to-red-500 flex items-center justify-center shadow-lg">
                      <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                  </Button>
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 rounded-lg hover:bg-red-100 transition-colors touch-manipulation"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-red-700" />
                ) : (
                  <Menu className="w-6 h-6 text-red-700" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Search - Visible only on xs screens */}
          {onSearch && (
            <div className="sm:hidden mt-3">
              <div 
                className="relative cursor-pointer"
                onClick={onSearchClick}
              >
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-600 dark:text-red-400 w-4 h-4 pointer-events-none" />
                <div className="pl-9 pr-3 py-2 text-sm rounded-full border-2 border-red-200 dark:border-red-900 bg-white dark:bg-gray-800 dark:text-gray-100 shadow-md w-full text-gray-500 dark:text-gray-400">
                  Search trainers, location, specialization...
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Drawer */}
      <nav
        id="mobile-menu"
        className={`fixed top-0 right-0 h-full w-64 sm:w-80 bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label="Mobile navigation"
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b-2 border-red-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <h2 className="text-lg font-bold text-red-700 dark:text-red-400">Menu</h2>
            <div className="flex items-center gap-2">
              {/* Theme Toggle for Mobile */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-red-700 dark:text-red-400" />
                ) : (
                  <Sun className="w-5 h-5 text-red-700 dark:text-red-400" />
                )}
              </button>
              <button
                onClick={closeMobileMenu}
                className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-6 h-6 text-red-700 dark:text-red-400" />
              </button>
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex-1 overflow-y-auto py-4">
            {/* Navigation Links */}
            <div className="px-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={item.path === '/' ? () => { closeMobileMenu(); handleHomeClick(); } : closeMobileMenu}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors touch-manipulation ${
                      item.highlight
                        ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white hover:from-red-600 hover:to-orange-600'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-gray-700 hover:text-red-700 dark:hover:text-red-400'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Cart Section */}
            <div className="mt-4 px-4">
              <Link
                to="/shop/cart"
                onClick={closeMobileMenu}
                className="flex items-center justify-between px-4 py-3 rounded-lg font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-700 hover:to-red-600 transition-colors touch-manipulation shadow-lg"
              >
                <div className="flex items-center gap-3">
                  <ShoppingCart className="w-5 h-5" />
                  <span>Shopping Cart</span>
                </div>
                <div className="flex items-center justify-center bg-white text-red-700 text-xs font-bold rounded-full min-w-[60px] h-7 px-2 shadow-md">
                  {cart?.item_count || 0} {(cart?.item_count || 0) === 1 ? 'item' : 'items'}
                </div>
              </Link>
            </div>

            {/* User Section */}
            <div className={`mt-6 px-4 pt-6 border-t-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              {user ? (
                <div className="space-y-2">
                  <div className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-red-50'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Signed in as</p>
                    <p className={`font-semibold ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>{user.name}</p>
                    <p className={`text-xs capitalize ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{user.role}</p>
                  </div>
                  
                  {/* User Menu Items */}
                  {getUserMenuItems(user).map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors touch-manipulation ${
                          theme === 'dark' 
                            ? 'text-gray-100 bg-gray-700/50 hover:bg-gray-600 hover:text-red-300' 
                            : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`} />
                        {item.label}
                      </Link>
                    );
                  })}
                  
                  <button
                    onClick={handleLogout}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-semibold transition-colors touch-manipulation ${
                      theme === 'dark' 
                        ? 'text-red-400 hover:bg-red-900/30' 
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block w-full px-4 py-3 bg-gradient-to-r from-red-700 to-red-600 text-white font-semibold rounded-lg text-center hover:from-black hover:to-red-800 transition-colors touch-manipulation"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="block w-full px-4 py-3 border-2 border-red-700 text-red-700 font-semibold rounded-lg text-center hover:bg-red-50 transition-colors touch-manipulation"
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default ResponsiveHeader;
